import { openStepUpModal } from './modal'
import { renderStepUpInline } from './inline'
import type { StepUpFormOptions, StepUpResult } from './form'
import { StepUpError } from './errors'
import { getZIdentityConfig } from '../config'

export { StepUpError } from './errors'
export type { StepUpErrorCode } from './errors'
export type { StepUpResult, SubmitCodeParams } from './form'

export interface StepUpOptions extends Omit<StepUpFormOptions, 'authority' | 'clientId' | 'accessToken'> {
  /** Falls back to the authority set via configureZIdentity() when omitted. */
  authority?: string
  /** Falls back to the clientId set via configureZIdentity() when omitted. */
  clientId?: string
  /** Falls back to configureZIdentity()'s getAccessToken() when omitted. */
  accessToken?: string
  /** Where the form is rendered. Defaults to 'modal'. */
  mode?: 'modal' | 'inline'
  /** Required when mode is 'inline' — the element the form is injected into. */
  target?: HTMLElement
}

// Deliberately not `async`: every check below is an integration mistake (missing/inconsistent
// options), and those must keep throwing synchronously — same contract as before this function
// gained a global-config fallback — so a caller building `options` dynamically can tell "I made
// a mistake calling this" apart from "the flow itself rejected". Only the actual accessToken
// resolution (the one part that may need to await getAccessToken()) happens further down, in
// resolveAndStart — an `async function` here would turn every throw below into a Promise
// rejection instead.
export function prompt2FA(options: StepUpOptions = {}): Promise<StepUpResult> {
  const config = getZIdentityConfig()
  const authority = options.authority ?? config?.authority
  const clientId = options.clientId ?? config?.clientId

  if (!authority) {
    throw new Error('prompt2FA: "authority" is required (pass it directly, or set it via configureZIdentity)')
  }
  if (!clientId) {
    throw new Error('prompt2FA: "clientId" is required (pass it directly, or set it via configureZIdentity)')
  }

  const mode = options.mode ?? 'modal'

  if (mode === 'inline') {
    if (!options.target) {
      throw new Error('prompt2FA: "target" is required when mode is "inline"')
    }
  } else if (options.target) {
    throw new Error('prompt2FA: "target" is only used when mode is "inline"')
  }

  if (!options.accessToken && !config?.getAccessToken) {
    throw new Error(
      'prompt2FA: "accessToken" is required (pass it directly, or set "getAccessToken" via configureZIdentity)',
    )
  }

  return resolveAndStart(options, authority, clientId, mode, config?.getAccessToken)
}

async function resolveAndStart(
  options: StepUpOptions,
  authority: string,
  clientId: string,
  mode: 'modal' | 'inline',
  getAccessToken: (() => string | Promise<string>) | undefined,
): Promise<StepUpResult> {
  let accessToken = options.accessToken
  if (!accessToken) {
    try {
      // Presence of at least one of these two was already guaranteed by prompt2FA() above.
      accessToken = await getAccessToken!()
    } catch (err) {
      throw new StepUpError('invalid_access_token', `prompt2FA: getAccessToken() failed: ${String(err)}`)
    }
    if (!accessToken) {
      throw new StepUpError('invalid_access_token', 'prompt2FA: getAccessToken() did not return a token.')
    }
  }

  const resolvedOptions: StepUpFormOptions = { ...options, authority, clientId, accessToken }

  if (mode === 'inline') {
    return renderStepUpInline(options.target!, resolvedOptions)
  }
  return openStepUpModal(resolvedOptions)
}
